/**
 * Parsed Claude Code weekly-usage banner observation.
 *
 * Claude Code renders one of two banner variants in its UI:
 *
 *   1. Percentage form (approaching limit):
 *      "You've used 87% of your weekly limit · resets Nov 28"
 *
 *   2. Saturated form (already at limit, ENG-5434):
 *      "You've hit your limit · resets May 26, 5pm (UTC)"
 *
 *   3. Weekly saturated form (ENG-7904): the phrasing carries a qualifier
 *      ("weekly") and renders only the reset time-of-day, no date:
 *      "You've hit your weekly limit · resets 1am (UTC)"
 *
 * The reset date in the banner carries no year; we resolve it to the
 * occurrence nearest to `now` (prev/current/next year), which keeps the
 * resolved instant stable across the reset boundary (ENG-6416). The pct is
 * 0-100 inclusive;
 * the saturated form is reported as `pct = 100`. When the banner gives
 * an explicit time-of-day (saturated form), `weekResetsAt` carries that
 * exact UTC hour; the percentage form has no time component and falls
 * back to UTC midnight.
 *
 * Patterns are written defensively because the exact phrasing has
 * shifted across CC versions and we may need to add variants without
 * also having to revisit every call-site.
 */
import {
  parseResetDateTime,
  RESET_DATE_SOURCE,
  type ResetPrecision,
} from './reset-time.js';

/**
 * ENG-9394 moved `ResetPrecision` and the reset-string resolver into
 * `reset-time.ts`, so the `/usage` report parser shares ONE implementation of
 * the year-inference and precision rules rather than growing a second copy that
 * would drift (see that file for why the drift matters - ENG-8472).
 *
 * Re-exported here because it has been part of this module's public surface
 * since ENG-8901 and callers import it from `banner-parser`.
 */
export type { ResetPrecision } from './reset-time.js';

/**
 * ENG-9007: which usage window a banner is reporting.
 *
 * `unknown` is a real answer, not a placeholder. A qualifier we do not
 * recognise must not be silently read as `weekly` - that is the defect this
 * type exists to fix - but nor may an unrecognised word suppress a genuine
 * weekly cap. So `unknown` is carried through and alerted on like `weekly`,
 * while being labelled honestly in the message so the next reader can see the
 * parser met a word it did not know.
 */
export type LimitScope = 'weekly' | 'session' | 'spend' | 'org_spend' | 'unknown';

/**
 * Qualifiers that denote a SHORT, self-clearing window rather than the weekly
 * cap. Anchored on wording Claude Code actually renders ("session", "5-hour"),
 * plus the generic `<n>-hour` / `<n>h` shapes so a change from 5 to 6 hours
 * does not silently reclassify a session as a week.
 */
const SESSION_QUALIFIER = /^(?:session|\d{1,2}\s*-?\s*hours?|\d{1,2}h)$/i;

/**
 * ENG-10365: the Claude Team seat's INDIVIDUAL SPEND LIMIT, which is a third
 * ceiling and not a variant of the other two.
 *
 * It stops the agent exactly as hard as the weekly cap, but the remedy is
 * different in kind: a weekly cap lifts when its window rolls over, a spend
 * limit lifts when a human raises the budget (`/usage-credits`) or, on the seat
 * observed in ENG-10365, when the account's weekly window resets. Rendering it
 * as "weekly" tells a responder to wait for a boundary that may buy them
 * nothing, which is the same mislabelling ENG-9007 was raised to end one scope
 * earlier.
 *
 * "individual" is optional so a phrasing drift to a bare "spend limit" still
 * classifies rather than falling to `unknown` — the ENG-9007 lesson about
 * qualifiers the parser half-recognises.
 *
 * ENG-10474: a period word ("monthly spend", "individual monthly spend") is
 * admitted too. That guard above was against drift by SUBTRACTION (a word
 * dropped); production then showed drift by SUBSTITUTION (a different word in
 * the same slot), so the period is optional rather than enumerated per banner.
 */
const SPEND_QUALIFIER = /^(?:individual\s+)?(?:(?:daily|weekly|monthly)\s+)?spend$/i;

/**
 * ENG-10474: an ORG-WIDE spend ceiling — "You've hit your org's monthly spend
 * limit" — which is a fourth ceiling, not the individual one above.
 *
 * Folding it into `spend` would page with the wrong remedy. The individual
 * alert tells a responder to raise THIS SEAT's limit; an org monthly limit
 * stops every agent in the org at once (alyve's dwight and vera together), and
 * the admin action is on the org, not a seat. That blast radius is the ENG-9965
 * failure mode with a billing trigger, so it gets its own scope and its own
 * wording rather than a label that points the responder at one agent.
 *
 * Checked BEFORE `SPEND_QUALIFIER` in `classifyLimitScope`, because both end in
 * "spend". The possessive accepts both apostrophes: the pane mixes them.
 */
const ORG_SPEND_QUALIFIER =
  /^(?:your\s+)?(?:org|organi[sz]ation|team|workspace)['’]s\s+(?:(?:daily|weekly|monthly)\s+)?spend$/i;

/** Classify a captured qualifier. Absent means the weekly cap, as it always did. */
export function classifyLimitScope(qualifier: string | null | undefined): LimitScope {
  const q = (qualifier ?? '').trim();
  if (!q) return 'weekly';
  if (SESSION_QUALIFIER.test(q)) return 'session';
  if (ORG_SPEND_QUALIFIER.test(q)) return 'org_spend';
  if (SPEND_QUALIFIER.test(q)) return 'spend';
  if (/^weekly$/i.test(q)) return 'weekly';
  return 'unknown';
}

export interface UsageBannerObservation {
  /** 0-100 inclusive. Saturated form ('hit your limit') reports 100. */
  pct: number;
  /**
   * Reset moment inferred from the banner. UTC midnight when the
   * banner doesn't include a time-of-day; the exact UTC hour when it
   * does (saturated form, ENG-5434).
   *
   * ENG-8901: check `resetPrecision` before presenting this to anybody. For the
   * date-less weekly banner it is a reconstruction, not a reading.
   */
  weekResetsAt: Date;
  /** ENG-8901: whether `weekResetsAt` was read from the banner or reconstructed. */
  resetPrecision: ResetPrecision;
  /**
   * ENG-9007: WHICH limit the banner was about.
   *
   * The saturated banner carries an optional qualifier - "weekly", "session",
   * "5-hour" - and the parser used to swallow it with a non-capturing group and
   * record everything into a field named `weekResetsAt`. So
   * `You've hit your session limit` and `You've hit your weekly limit` produced
   * an identical CRITICAL claiming a WEEKLY cap. Session limits are hit far more
   * often, so most of those alerts were about a pause of a few hours.
   */
  limitScope: LimitScope;
  /**
   * The qualifier exactly as it appeared, or null when the banner had none.
   * Surfaced so an alert can quote what it parsed and a reader can falsify the
   * classification without opening the source (ENG-9007 criterion 5).
   */
  limitQualifier: string | null;
}

// Accepts: "You've used", "You’ve used", "You have used", or bare "used".
// Separator class covers ASCII "-", em/en dash, and the U+00B7 middle dot
// Claude Code uses today.
const SEP = /[\s·\-–—]+/.source;
const SUBJECT = /(?:You(?:['’]ve|\s+have)?\s+)?/.source;
// Reset target in the banner - "Mon DD" with an optional time tail, or a bare
// date-less time (ENG-7904). Shared with the /usage report parser via
// `reset-time.ts`; the captured string is resolved by `parseResetDateTime`.
const RESET_DATE = RESET_DATE_SOURCE;

/**
 * Known banner regex variants. The capture-groups are (pct | null, "<reset>").
 */
const BANNER_PATTERNS: readonly RegExp[] = [
  // Percentage form — pct in group 1, reset in group 2.
  new RegExp(
    `${SUBJECT}used\\s+(\\d{1,3})%\\s+of\\s+your\\s+weekly\\s+limit${SEP}resets\\s+(${RESET_DATE})`,
    'i',
  ),
  // Saturated form (ENG-5434, extended by ENG-7904) — no pct, reset in group 1.
  // An optional qualifier word ("weekly", "5-hour", …) may sit between "your"
  // and "limit"; matching it keeps the parser resilient to CC's phrasing drift.
  // Wrapped to keep the per-pattern shape consistent with the percentage form:
  // the parser checks group 1 for a digit string and treats a missing one as
  // pct=100.
  // ENG-9007: the qualifier is CAPTURED now, by name. It used to be
  // `(?:[a-z0-9-]+\s+)?` - permissive and non-capturing - so the parser
  // recognised "session" and "5-hour", matched on them, and threw the word away.
  // Named groups keep the numbered capture indices stable for the percentage
  // form above, which still reads groups 1 and 2 positionally.
  new RegExp(
    // CodeRabbit on #4621: the qualifier was ONE token, so `classifyLimitScope`
    // accepted "5 hours" while this pattern could never deliver it — the whole
    // banner failed to match, no observation was recorded, and the agent went
    // dark rather than merely being misclassified. Multi-token qualifiers now
    // match the duration shapes SESSION_QUALIFIER already accepts.
    //
    // Deliberately NOT the naive `[a-z0-9]+(?:\s*-?\s*[a-z0-9]+)*`: the
    // separator there can match empty, which reduces to `(a+)+` and backtracks
    // catastrophically on a long alphanumeric run that is never followed by
    // "limit". This parser runs over agent-produced pane.log tails, so that is
    // reachable input. Requiring at least one separator per extra token, and
    // bounding the count, keeps it linear.
    //
    // ENG-10474: the token class admits an apostrophe (both ' and ’). It was
    // `[a-z0-9]`, so ANY possessive qualifier ("org's monthly", "team's") made
    // the whole banner unmatchable, not merely unclassified: null, no
    // observation, no alert. The apostrophe cannot match empty, so the
    // at-least-one-separator shape above still keeps this linear.
    `${SUBJECT}hit\\s+your\\s+(?<qualifier>[a-z0-9'’]+(?:[\\s-]+[a-z0-9'’]+){0,3}\\s+)?limit${SEP}resets\\s+(?<reset>${RESET_DATE})`,
    'i',
  ),
  // ENG-10365 — the Claude Team seat's INDIVIDUAL SPEND LIMIT.
  //
  // WHY THE TWO PATTERNS ABOVE CANNOT MATCH IT. Both require `limit` to be
  // followed immediately by a separator and then `resets`. The spend banner puts
  // its remedy between the two:
  //
  //   You've hit your individual spend limit · run /usage-credits to ask your
  //   admin for a higher limit · your weekly limit resets Sep 12, 5pm (UTC)
  //   Continuing automatically at Sep 12, 5pm · esc to cancel
  //
  // so the adjacency fails and `parseUsageBanner` returned null — verified
  // against origin/main on the verbatim pane capture in ENG-10365 before this
  // pattern was written. Null means NO observation is POSTed, which means the
  // `claude_code_usage_observations` row never exists, which means
  // `usage-limit-monitor` never hears of the agent at all. cindy ran zero-second
  // turns for ~32 hours with `heartbeat_verdict fresh` and no usage alert of any
  // kind, because the whole chain starts here.
  //
  // THE GAP IS BOUNDED AND LAZY, deliberately. The real banner's gap is ~78
  // characters; 160 leaves 2x headroom for phrasing drift and for the pane's own
  // line wrap (which lands mid-sentence, as above, so `[\s\S]` and not `.`).
  // Lazy + bounded keeps it linear on the pane.log tails this parser runs over —
  // the same reason the saturated pattern refuses the naive `(a+)+` shape.
  //
  // WHAT IT INHERITS BY MATCHING AT ALL. Everything downstream keys on the
  // `usage_limit_reached` ALERT KIND rather than on the scope, so one matching
  // banner reaches the roster's `usage_limit_alert_open` flag (ENG-9634),
  // `resolveCappedAgents`, and both monitors that suppress on a capped agent.
  // That is why this fix is a regex and not a subsystem.
  //
  // AND A SECOND CONSUMER, which is worth stating precisely because it is easy
  // to overclaim. `rate-limit-classifier.ts:160` calls this parser to read the
  // RESET TIME out of a `rate_limit` / 429 transcript refusal, and
  // `readUsageCapUntil` (agent-serving-probe.ts:296) returns null — i.e. does NOT
  // defer — when that reset comes back null. So a parse failure here has always
  // disarmed the ENG-8197 host-side dispatch deferral for this banner, which
  // would explain cindy being dispatched into hourly while visibly blocked.
  //
  // NOT VERIFIED, and deliberately not claimed: whether a spend refusal is
  // written to the transcript as `error: 'rate_limit'` / `apiErrorStatus: 429`
  // at all. The classifier's verdict keys on those fields, not on this text. If
  // it is, the deferral starts working as a side effect of this fix; if it is
  // not, ENG-8197 is still blind to the spend ceiling and that is a separate
  // ticket. Nothing here depends on which.
  //
  // KNOWN LIMIT, stated rather than papered over: a spend banner that carries no
  // `resets` clause at all still yields null. `week_resets_at` drives the
  // monitor's auto-close, and synthesising an instant we never read is the exact
  // confident-and-false reporting ENG-8901 exists to stop. Every spend banner
  // observed so far carries one.
  //
  // ENG-10474 — THE CAPTURE NO LONGER GATES THE CLASSIFIER. It admitted exactly
  // `spend` or `individual spend`, so "You've hit your org's monthly spend
  // limit" failed here as well as in both patterns above and alyve's agents went
  // dark with every platform signal green. The capture now takes up to three
  // qualifier tokens (apostrophes allowed) in front of `spend`, and
  // `classifyLimitScope` decides what they mean, which is how pattern 2 already
  // worked.
  //
  // `spend` STAYS THE ANCHOR, and that is the design decision. Making this
  // capture as open as pattern 2's would turn it into "any saturated banner with
  // up to 160 characters before `resets`", so "You've hit your weekly limit"
  // followed by pane prose and an unrelated "resets Nov 28" would start
  // producing observations — the false-pairing surface the 160-character bound
  // exists to fence. Pattern 2 keeps its adjacency rule for every non-spend
  // qualifier; this pattern only widens WHICH spend. Pinned by a test either way.
  new RegExp(
    `${SUBJECT}hit\\s+your\\s+(?<qualifier>(?:[a-z0-9'’]+[\\s-]+){0,3}spend)\\s+limit[\\s\\S]{0,160}?resets\\s+(?<reset>${RESET_DATE})`,
    'i',
  ),
];

/**
 * Parse a chunk of text (typically the tail of `pane.log`) for the
 * Claude Code weekly-usage banner. Returns the **most-recent** banner in
 * the text (the match with the greatest position), or null if no banner
 * is present.
 *
 * Why most-recent and not first (ENG-6284): the banner renders only
 * intermittently in the Claude Code pane, so the host scraper now reads a
 * wide pane.log tail to reliably catch it. A wide window can contain the
 * full climb of banners over the week ("75% …", "76% …", … "100% …"); the
 * latest one is the agent's current usage, so we must return the last
 * occurrence — returning the first would report a stale, lower pct and
 * could mask an at-limit agent.
 */
export function parseUsageBanner(
  text: string,
  now: Date = new Date(),
): UsageBannerObservation | null {
  let bestIndex = -1;
  let best: UsageBannerObservation | null = null;

  for (let i = 0; i < BANNER_PATTERNS.length; i++) {
    // Clone with the global flag so we can scan every occurrence, not just
    // the first. The shared BANNER_PATTERNS regexes are stateless ('i'
    // only); a per-call global clone keeps lastIndex local to this call.
    const pattern = new RegExp(BANNER_PATTERNS[i]!.source, 'gi');
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      // Guard against a zero-width match wedging the loop.
      if (match.index === pattern.lastIndex) pattern.lastIndex++;

      // Pattern 0 is the percentage form (pct in group 1, reset in group 2).
      // Patterns 1 and 2 are the saturated forms — "hit your <qualifier> limit"
      // and ENG-10365's spend-limit variant — and both read their reset and
      // qualifier by NAME with pct implicit 100, so they share one branch. A new
      // saturated variant needs only a named-group pattern, never a new arm
      // here; that is the point of the named groups.
      let pct: number;
      let resetStr: string;
      let qualifier: string | null;
      if (i === 0) {
        pct = Number.parseInt(match[1]!, 10);
        resetStr = match[2]!;
        // The percentage form says "of your weekly limit" in the pattern itself,
        // so its scope is not in doubt.
        qualifier = 'weekly';
      } else {
        pct = 100;
        resetStr = match.groups?.['reset'] ?? '';
        qualifier = match.groups?.['qualifier']?.trim() || null;
      }
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) continue;

      // ENG-8194: reject a banner that runs straight into more text.
      //
      // pane.log carries whatever is on the agent's screen, and the scraper
      // cannot tell Claude Code's own status render from the AGENT WRITING
      // ABOUT the banner. On agt-aws-1 sherlock quoted it verbatim while
      // diagnosing a different agent - "...every turn returns You've hit your
      // weekly limit - resets 4pm (UTC) in under a second..." - the manager
      // scraped its own agent's prose, armed the usage-limit marker, and the
      // channel MCPs answered every human with the canned "hit its limit"
      // notice instead of dispatching. Self-gating, and self-REINFORCING: the
      // more the agent discusses being capped, the longer it stayed capped.
      //
      // The discriminator comes from real captures, not intuition. ANSI
      // column-positioning escapes are stripped before we see the text, so a
      // GENUINE banner is also glued to whatever precedes it on the status line
      // ("...<- for agentsYou've used 83% of your weekly limit - resets 7pm
      // (UTC)"). A leading-boundary test would therefore reject real banners.
      // What separates them is the TRAILING side: a real render ends at a line
      // break, a stripped escape, or end-of-buffer, while quoted prose
      // continues into the next word ("(UTC)inunderasecond").
      //
      // ENG-8208 KEPT this, against that issue's own acceptance criteria.
      //
      // The issue's rationale was that the guard "exists only to stop a quoted
      // banner arming the marker", and Slice 4 deleted the marker. "Only" is the
      // incorrect word: the guard does not live in the marker path, it lives in
      // `parseUsageBanner`, which had TWO consumers. The other one — the
      // ENG-5389 / ENG-6183 usage OBSERVATION posted to /host/usage-observations
      // — is still very much alive.
      //
      // And it is not self-limiting on that path. This function returns the
      // LATEST-positioned match, the monitor reads a 5,000-line tail, and the
      // observation POST is deduped on change — so while an agent's prose about
      // the limit is the last banner-shaped thing in the pane epoch, it IS that
      // agent's reported usage, and it stands until a genuine banner appears
      // further down the buffer or the prose scrolls out. The alert cron
      // re-derives at-limit state from the latest observation each tick.
      //
      // Deleting it would therefore have traded a demonstrated false-positive
      // vector for nothing: no behaviour is unblocked, nothing is simplified,
      // and there is no runtime cost. It errs toward NOT reporting, which is the
      // safe direction for the observation path exactly as it was for the
      // marker. Heuristic, not a proof — a real banner immediately followed by
      // more status text with no separator is dropped — which was an accepted
      // trade when it was written and is unchanged.
      const nextChar = text[match.index + match[0]!.length];
      if (nextChar !== undefined && /[A-Za-z0-9]/.test(nextChar)) continue;

      const reset = parseResetDateTime(resetStr, now);
      if (!reset) continue;

      // Keep the latest-positioned valid banner across both patterns.
      if (match.index >= bestIndex) {
        bestIndex = match.index;
        best = {
          pct,
          weekResetsAt: reset.at,
          resetPrecision: reset.precision,
          limitScope: classifyLimitScope(qualifier),
          limitQualifier: qualifier,
        };
      }
    }
  }

  return best;
}
